import { useEffect, useRef, useState } from 'react';
import { api, type PlaceHit } from '../api';

/**
 * Towns near the tenant, for naming what a projected storm path crosses.
 *
 * Deliberately isolated from everything else on the dashboard. This is the only Census
 * lookup on the live path during a warning, and if it is slow or down, site arrival
 * times — which are computed locally from data already in hand — must be unaffected.
 * So: its own hook, its own failure, and the last good list is kept rather than blanked
 * while a new one is in flight.
 */
const PAD_DEG = 1.0;
const REFRESH_MS = 10 * 60_000;

export function usePlaces(centre: [number, number] | null, enabled = true): PlaceHit[] {
  const [places, setPlaces] = useState<PlaceHit[]>([]);
  const keyRef = useRef<string | null>(null);

  // Snapped, so panning or a drifting anchor keeps hitting the same server-side entry.
  const bbox: [number, number, number, number] | null = centre
    ? [
        Math.round((centre[0] - PAD_DEG) * 4) / 4,
        Math.round((centre[1] - PAD_DEG) * 4) / 4,
        Math.round((centre[0] + PAD_DEG) * 4) / 4,
        Math.round((centre[1] + PAD_DEG) * 4) / 4,
      ]
    : null;
  const key = enabled && bbox ? bbox.join(',') : null;

  useEffect(() => {
    if (!key || !bbox) return;
    let cancelled = false;
    keyRef.current = key;
    const load = async () => {
      try {
        const r = await api.geoPlaces(bbox);
        if (!cancelled && keyRef.current === key) setPlaces(Array.isArray(r?.places) ? r.places : []);
      } catch {
        // Keep whatever we had. A failed town lookup must never blank the panel.
      }
    };
    void load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return places;
}
